import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import StudentsWaitingTable from '@/tables/coordinator/StudentsWaitingTable';
import { useCoordinatorGroupAccess } from '~/hooks/useIsCoordinatorGroup';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/students-waiting',
)({
  // L23's drill-in, same shape and same reason as the All Students route.
  validateSearch: (search): { study_helper_id?: number } => {
    const id = Number(search.study_helper_id);
    return {
      study_helper_id: Number.isInteger(id) && id > 0 ? id : undefined,
    };
  },
  component: StudentsWaitingScreen,
});

function StudentsWaitingScreen(): ReactElement | null {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { study_helper_id: deepLinkedHelperId } = Route.useSearch();
  const navigate = useNavigate();

  // A deliberate divergence, W3.9 X15: legacy SERVES this route on a
  // study-helper group (measured, gid 512, 45 rows) and never links it — the
  // subnav item is registered by `custom-study-group-students-tab.php:22-25`
  // alone, and `study-group-students-tab.php:72-75` has its link commented out.
  // Web already matches legacy on the nav (`useStudentTabs.ts`), so without the
  // guard this page paints rows while its own filter panel 403s:
  // `congregation-students-waiting`'s Requirement is congregation-scoped. A
  // route with no way in is untested surface.
  //
  // Group TYPE only — the membership axis is the Students layout's
  // (`$groupName.students.tsx:39-57`) and it has already run. `isResolved` is
  // not optional: while `getGroupBySlug` is in flight a bare
  // `!isCoordinatorGroup` bounces the group's own coordinator.
  const { isCoordinatorGroup, isResolved } = useCoordinatorGroupAccess();
  const refused = isResolved && !isCoordinatorGroup;

  useEffect(() => {
    if (refused) {
      // All Students, not the group home: the caller passed the Students tab's
      // own gate, and All Students is the first subnav on every extension —
      // where `load_default_subnav_screen` (`WBS_Group_Extension.php:329-335`)
      // sends a caller with no valid subnav.
      void navigate({
        to: '/groups/$groupName/students/all-students/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  // Seeded once, then stripped — legacy reads the hash into the request and
  // clears it (`wbs-datatable.js:583-594`), never restoring it into the panel.
  const [seededHelperId] = useState(() =>
    deepLinkedHelperId ? [deepLinkedHelperId] : undefined,
  );
  useEffect(() => {
    if (deepLinkedHelperId === undefined) return;
    void navigate({
      to: '/groups/$groupName/students/students-waiting/',
      params: { groupName },
      search: { study_helper_id: undefined },
      replace: true,
    });
  }, [deepLinkedHelperId, groupName, navigate]);

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('lessons.students-waiting')} />
      </GroupHeaderWrapper>
      <StudentsWaitingTable
        groupId={groupId}
        groupType={groupData?.group_type ?? undefined}
        seededHelperId={seededHelperId}
      />
    </>
  );
}
